import { Layout } from "providers/Layout";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BaseInput,
  Button,
  ButtonSize,
  ButtonVariant,
  Show
} from "shared/components";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { Card } from "shared/components/Card";
import { useSettingsController } from "views/settings-integration/controller";

// Paste-in Google Ads conversion tracking (CRM-952): a Conversion ID shared by
// both goals plus one Conversion Label per goal ("Contact Form Submitted" /
// "Showing Booked"). The public listing pages read these settings and fire a
// gtag conversion with send_to "conversionId/label" at the matching moment.
export const GoogleAdsView = () => {
  const navigate = useNavigate();
  const { updateSettings, currentSettings, isUpdateSettingsPending } =
    useSettingsController();

  const [conversionId, setConversionId] = useState("");
  const [contactFormLabel, setContactFormLabel] = useState("");
  const [showingLabel, setShowingLabel] = useState("");

  const saved = currentSettings.googleAds;

  const hasChanges = useMemo(
    () =>
      conversionId.trim() !== (saved.conversionId ?? "") ||
      contactFormLabel.trim() !== (saved.contactFormConversionLabel ?? "") ||
      showingLabel.trim() !== (saved.showingConversionLabel ?? ""),
    [
      conversionId,
      contactFormLabel,
      showingLabel,
      saved.conversionId,
      saved.contactFormConversionLabel,
      saved.showingConversionLabel
    ]
  );

  // A Conversion ID with no label can complete no goal — the listing pages
  // deliberately load no Ads config for it, so block this silently-inert state
  // here. Clearing ALL fields stays allowed: that is the disconnect path.
  const isInert = Boolean(
    conversionId.trim() && !contactFormLabel.trim() && !showingLabel.trim()
  );

  const handleSave = () => {
    const trimmedId = conversionId.trim();
    const trimmedContactFormLabel = contactFormLabel.trim();
    const trimmedShowingLabel = showingLabel.trim();

    updateSettings({
      googleAdsConversionId: trimmedId || null,
      googleAdsContactFormConversionLabel: trimmedContactFormLabel || null,
      googleAdsShowingConversionLabel: trimmedShowingLabel || null
    }).then(() => {
      if (!trimmedId) navigate("/settings/integration");
    });
  };

  const handleCancel = () => {
    setConversionId(saved.conversionId ?? "");
    setContactFormLabel(saved.contactFormConversionLabel ?? "");
    setShowingLabel(saved.showingConversionLabel ?? "");
  };

  useEffect(() => {
    setConversionId(saved.conversionId ?? "");
    setContactFormLabel(saved.contactFormConversionLabel ?? "");
    setShowingLabel(saved.showingConversionLabel ?? "");
  }, [
    saved.conversionId,
    saved.contactFormConversionLabel,
    saved.showingConversionLabel
  ]);

  return (
    <Layout
      header={
        <>
          <div className="flex items-center justify-between border-b border-alpha-40 px-3 md:px-6 pb-3">
            <BreadCrumbs
              crumbs={[
                { title: "Settings", href: "/settings" },
                { title: "Integration", href: "/settings/integration" },
                {
                  title: "Google Ads",
                  href: "/settings/integration/google-ads"
                }
              ]}
            />
          </div>
          <h1 className="text-xl font-bold text-content-1000 p-6 pb-3">
            Google Ads
          </h1>
        </>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            onClick={handleCancel}
            className="w-[125px]"
            disabled={!hasChanges || isUpdateSettingsPending}
          >
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SM}
            onClick={handleSave}
            className="w-[125px]"
            disabled={!hasChanges || isInert || isUpdateSettingsPending}
            loading={isUpdateSettingsPending}
          >
            Save
          </Button>
        </div>
      }
    >
      <Card className="w-full p-6 max-w-[800px]">
        <p className="font-bold">Conversion tracking</p>
        <p className="mt-2 text-sm text-content-700">
          Report a conversion back to your Google Ads account whenever a visitor
          submits the Contact Us form or books a showing. Find the Conversion ID
          and Labels in Google Ads under Goals → Conversions → your conversion
          action → Tag setup.
        </p>

        <div className="mt-6 border-t border-alpha-10 pt-6 flex flex-col gap-4">
          <BaseInput
            value={conversionId}
            onChange={setConversionId}
            id="google-ads-conversion-id"
            label="Conversion ID"
            placeholder="AW-123456789"
            required
          />
          <BaseInput
            value={contactFormLabel}
            onChange={setContactFormLabel}
            id="google-ads-contact-form-label"
            label="Conversion Label — Contact Form Submitted"
            placeholder="AbCdEfGhIj"
          />
          <BaseInput
            value={showingLabel}
            onChange={setShowingLabel}
            id="google-ads-showing-label"
            label="Conversion Label — Showing Booked"
            placeholder="KlMnOpQrSt"
          />
          <Show if={isInert}>
            <p className="text-sm text-content-700">
              Add at least one Conversion Label — without a label no conversion
              can be reported to Google Ads. To disconnect the integration,
              clear all fields.
            </p>
          </Show>
        </div>
      </Card>
    </Layout>
  );
};
